import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import { styled } from "@mui/material/styles";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";
import api from "../../services/api";

const MainPaper = styled(Paper)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2),
  margin: theme.spacing(1),
  overflowY: "auto",
  ...theme.scrollbarStyles,
}));

// "143" vira "2h23"; sem resposta no período vira travessão.
const formatDelay = (minutes) => {
  if (minutes == null) return "—";
  if (minutes < 60) return `${minutes}min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h}h${String(m).padStart(2, "0")}` : `${h}h`;
};

// A data vem como YYYY-MM-DD (ou ISO); mostra dd/mm sem depender de fuso.
const formatDay = (day) => {
  const iso = String(day).slice(0, 10);
  const [y, m, d] = iso.split("-");
  return d ? `${d}/${m}/${y}` : iso;
};

const StatCard = ({ label, value, sub, color }) => (
  <Card variant="outlined" sx={{ height: "100%" }}>
    <CardContent sx={{ textAlign: "center", py: 2 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, color: color || "text.primary" }}>
        {value}
      </Typography>
      <Typography variant="body2" color="textSecondary">{label}</Typography>
      {sub != null && (
        <Typography variant="caption" sx={{ color: color || "text.secondary" }}>{sub}</Typography>
      )}
    </CardContent>
  </Card>
);

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

const MessageStatusReport = () => {
  const [startDate, setStartDate] = useState(daysAgo(7));
  const [endDate, setEndDate] = useState(today());
  const [whatsappId, setWhatsappId] = useState("");
  const [connections, setConnections] = useState([]);
  const [data, setData] = useState(null);
  const [disparos, setDisparos] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchConns = async () => {
      try {
        const { data } = await api.get("/whatsapp");
        setConnections(Array.isArray(data) ? data : []);
      } catch (err) {
        // silencioso
      }
    };
    fetchConns();
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = { startDate, endDate, whatsappId: whatsappId || undefined };
      const [status, disp] = await Promise.all([
        api.get("/reports/message-status", { params }),
        api.get("/reports/disparos", { params }),
      ]);
      setData(status.data);
      setDisparos(disp.data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportCsv = () => {
    if (!data) return;
    const rows = [["Contato", "Número", "Conexão", "Código", "Motivo", "Quando"]];
    data.failures.forEach((f) => {
      rows.push([
        f.contactName || "",
        f.number || "",
        f.connectionName || "",
        f.errorCode || "",
        (f.errorMessage || "").replace(/[\n;]/g, " "),
        f.failedAt ? new Date(f.failedAt).toLocaleString("pt-BR") : "",
      ]);
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `falhas_${startDate}_a_${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // quem respondeu aos disparos, para a equipe retornar
  const exportRespostasCsv = () => {
    if (!disparos) return;
    const rows = [["Disparo", "Dia", "Contato", "Número", "Resposta", "Quando"]];
    disparos.contacts.forEach((c) => {
      rows.push([
        c.disparo || "",
        formatDay(c.dia),
        c.contactName || "",
        c.number || "",
        (c.reply || "").replace(/[\n;]/g, " "),
        c.replyAt ? new Date(c.replyAt).toLocaleString("pt-BR") : "",
      ]);
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `respostas_disparos_${startDate}_a_${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const s = data?.summary;

  return (
    <MainContainer>
      <MainHeader>
        <Title>Relatório de mensagens (entrega / leitura / falhas)</Title>
      </MainHeader>
      <MainPaper variant="outlined">
        {/* Filtros */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", mb: 2 }}>
          <TextField
            label="De" type="date" size="small" value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Até" type="date" size="small" value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            select label="Conexão" size="small" value={whatsappId}
            onChange={(e) => setWhatsappId(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">Todas</MenuItem>
            {connections.map((c) => (
              <MenuItem key={c.id} value={c.id}>{c.name} ({c.type})</MenuItem>
            ))}
          </TextField>
          <Button variant="contained" onClick={fetchReport} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : "Gerar"}
          </Button>
          <Button variant="outlined" onClick={exportCsv} disabled={!data || data.failures.length === 0}>
            Exportar falhas (CSV)
          </Button>
        </Box>

        {/* Cards de totais */}
        {s && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={2.4}><StatCard label="Enviadas" value={s.total} /></Grid>
            <Grid item xs={6} md={2.4}><StatCard label="Entregues" value={s.delivered + s.read} sub={`${s.deliveredRate}%`} color="#2e7d32" /></Grid>
            <Grid item xs={6} md={2.4}><StatCard label="Lidas" value={s.read} sub={`${s.readRate}%`} color="#1565c0" /></Grid>
            <Grid item xs={6} md={2.4}><StatCard label="Respondidas" value={s.responded} sub={`${s.respondedRate}%`} color="#6a1b9a" /></Grid>
            <Grid item xs={6} md={2.4}><StatCard label="Falhas" value={s.failed} sub={`${s.failedRate}%`} color="#c62828" /></Grid>
          </Grid>
        )}

        {/* Disparos (campanhas da plataforma e disparos externos) e quem respondeu */}
        {disparos && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Disparos e respostas</Typography>
              <Button size="small" variant="outlined" onClick={exportRespostasCsv} disabled={!disparos.contacts.length}>
                Exportar quem respondeu (CSV)
              </Button>
            </Box>
            {disparos.disparos.length === 0 ? (
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Nenhum disparo no período.
              </Typography>
            ) : (
              <Table size="small" sx={{ mb: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Disparo</TableCell>
                    <TableCell>Dia</TableCell>
                    <TableCell align="center">Enviadas</TableCell>
                    <TableCell align="center">Não entregues</TableCell>
                    <TableCell align="center">Entregues</TableCell>
                    <TableCell align="center">Lidas</TableCell>
                    <TableCell align="center">Responderam</TableCell>
                    <TableCell>O que responderam</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {disparos.disparos.map((d) => (
                    <TableRow key={`${d.disparo}-${d.dia}`} sx={{ verticalAlign: "top" }}>
                      <TableCell>{d.disparo}<br /><span style={{ opacity: 0.6, fontSize: 12 }}>{d.connectionName}</span></TableCell>
                      <TableCell>{formatDay(d.dia)}</TableCell>
                      <TableCell align="center">{d.total}</TableCell>
                      <TableCell align="center" sx={{ color: d.failed > 0 ? "#c62828" : "inherit" }}
                        title={d.failureReasons.map((f) => `${f.total}: ${f.hint}`).join("\n")}>
                        {d.failed}
                        {d.failureReasons.slice(0, 2).map((f) => (
                          <div key={f.errorCode} style={{ fontSize: 11, opacity: 0.8 }}>{f.total} — {f.hint}</div>
                        ))}
                      </TableCell>
                      <TableCell align="center">{d.delivered}</TableCell>
                      <TableCell align="center">{d.read}</TableCell>
                      <TableCell align="center" sx={{ color: "#6a1b9a", fontWeight: 600 }}>
                        {d.responded} <span style={{ opacity: 0.7, fontWeight: 400 }}>({d.respondedRate}% das entregues)</span>
                      </TableCell>
                      <TableCell>
                        {d.topReplies.map((r) => (
                          <div key={r.texto} style={{ fontSize: 12 }}><b>{r.total}</b> {r.texto}</div>
                        ))}
                        {d.otherReplies > 0 && <div style={{ fontSize: 12, opacity: 0.7 }}>+{d.otherReplies} outras</div>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {disparos.disparos.length > 0 && (
              <Typography variant="caption" color="textSecondary" component="div" sx={{ mb: 3 }}>
                Conta como resposta a primeira mensagem do contato em até {disparos.replyWindowDays} dias depois do disparo.
                A porcentagem é sobre as entregues: quem não tem WhatsApp não tinha como responder.
              </Typography>
            )}
          </>
        )}

        {/* Por conexão */}
        {data?.byConnection?.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Por conexão</Typography>
            <Table size="small" sx={{ mb: 3 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Conexão</TableCell>
                  <TableCell align="center">Tipo</TableCell>
                  <TableCell align="center">Enviadas</TableCell>
                  <TableCell align="center">Entregues</TableCell>
                  <TableCell align="center">Lidas</TableCell>
                  <TableCell align="center">Respondidas</TableCell>
                  <TableCell align="center">Tempo até responder</TableCell>
                  <TableCell align="center">Falhas</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.byConnection.map((c) => (
                  <TableRow key={c.whatsappId}>
                    <TableCell>{c.connectionName}</TableCell>
                    <TableCell align="center">
                      <Chip size="small" label={c.connectionType === "waba" ? "Oficial" : c.connectionType} />
                    </TableCell>
                    <TableCell align="center">{c.total}</TableCell>
                    <TableCell align="center">{c.delivered + c.read}</TableCell>
                    <TableCell align="center">{c.read}</TableCell>
                    <TableCell align="center" sx={{ color: "#6a1b9a" }}>
                      {c.responded} <span style={{ opacity: 0.7 }}>({c.respondedRate}%)</span>
                    </TableCell>
                    <TableCell align="center">{formatDelay(c.replyMinutes)}</TableCell>
                    <TableCell align="center" sx={{ color: c.failed > 0 ? "#c62828" : "inherit" }}>{c.failed}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}

        {/* Por dia — a linha que falta (ou o total zerado) denuncia o dia em que o disparo não rodou */}
        {data?.byDay?.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Por dia</Typography>
            <Table size="small" sx={{ mb: 3 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Dia</TableCell>
                  <TableCell align="center">Enviadas</TableCell>
                  <TableCell align="center">Respondidas</TableCell>
                  <TableCell align="center">Falhas</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.byDay.map((d) => (
                  <TableRow key={d.day}>
                    <TableCell>{formatDay(d.day)}</TableCell>
                    <TableCell align="center">{d.total}</TableCell>
                    <TableCell align="center" sx={{ color: "#6a1b9a" }}>{d.responded}</TableCell>
                    <TableCell align="center" sx={{ color: d.failed > 0 ? "#c62828" : "inherit" }}>{d.failed}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}

        {/* Motivos de falha agrupados, com a explicação em português */}
        {data?.failureReasons?.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Por que falhou</Typography>
            <Table size="small" sx={{ mb: 3 }}>
              <TableHead>
                <TableRow>
                  <TableCell align="center">Qtd</TableCell>
                  <TableCell align="center">Código</TableCell>
                  <TableCell>O que significa</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.failureReasons.map((r) => (
                  <TableRow key={`${r.errorCode}-${r.errorMessage}`}>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>{r.total}</TableCell>
                    <TableCell align="center">{r.errorCode || "—"}</TableCell>
                    <TableCell>{r.hint}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}

        {/* Lista de falhas */}
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Mensagens não entregues {data ? `(${data.failures.length})` : ""}
        </Typography>
        {data && data.failures.length === 0 && (
          <Typography variant="body2" color="textSecondary">
            Nenhuma falha de entrega no período. 🎉
          </Typography>
        )}
        {data?.failures?.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Contato</TableCell>
                <TableCell>Número</TableCell>
                <TableCell>Conexão</TableCell>
                <TableCell>Motivo</TableCell>
                <TableCell>Quando</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.failures.map((f) => (
                <TableRow key={f.id}>
                  <TableCell>{f.contactName || "—"}</TableCell>
                  <TableCell>{f.number || "—"}</TableCell>
                  <TableCell>{f.connectionName}</TableCell>
                  <TableCell sx={{ color: "#c62828" }}>
                    {f.errorMessage || "Falha na entrega"}{f.errorCode ? ` (${f.errorCode})` : ""}
                  </TableCell>
                  <TableCell>{f.failedAt ? new Date(f.failedAt).toLocaleString("pt-BR") : "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </MainPaper>
    </MainContainer>
  );
};

export default MessageStatusReport;
